import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  Layout,
  Form,
  FormLayout,
  Checkbox,
  BlockStack,
  Text,
  Banner,
  Spinner,
  Select,
  TextField,
} from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { settingsApi } from '../utils/api';
import { isLocalDevelopment } from '../utils/env';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { shopId } = useAuth();
  // Moderation
  const [autoApprove, setAutoApprove] = useState(false);
  const [requireVerification, setRequireVerification] = useState(true);
  const [minCommentLength, setMinCommentLength] = useState(10);
  const [bannedWords, setBannedWords] = useState('');
  const [autoRejectRating, setAutoRejectRating] = useState('0');
  
  // Widget Display
  const [reviewOrder, setReviewOrder] = useState('newest');
  const [reviewsPerPage, setReviewsPerPage] = useState(10);
  const [showPhotos, setShowPhotos] = useState(true);
  const [showVerifiedBadge, setShowVerifiedBadge] = useState(true);
  const [showReviewTitle, setShowReviewTitle] = useState(true);
  
  // Review Form
  const [titleRequired, setTitleRequired] = useState(false);
  const [allowPhotoUpload, setAllowPhotoUpload] = useState(true);
  const [maxPhotos, setMaxPhotos] = useState(3);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!shopId) return;

      setLoading(true);
      const result = await settingsApi.get(shopId);
      
      // Verificar se é plano PRO primeiro
      let paid = false;
      if (!isLocalDevelopment()) {
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
          const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
          
          const response = await fetch(
            `${supabaseUrl}/functions/v1/billing?shopId=${shopId}`,
            {
              headers: {
                'apikey': supabaseAnonKey,
                'Authorization': `Bearer ${supabaseAnonKey}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            paid = data.plan === 'paid' && data.status === 'active';
            setIsPaid(paid);
          }
        } catch (err) {
          setIsPaid(false);
        }
      }

      // Carregar configurações
      if (result.data) {
        // Moderation
        setAutoApprove(result.data.auto_approve || false);
        if (paid) {
          setRequireVerification(result.data.require_verification === true);
        } else {
          setRequireVerification(true); // FREE sempre exige verificação
        }
        setMinCommentLength(result.data.min_comment_length || 10);
        setBannedWords(result.data.banned_words || '');
        setAutoRejectRating(result.data.auto_reject_rating || '0');
        
        // Widget Display
        setReviewOrder(result.data.review_order || 'newest');
        setReviewsPerPage(result.data.reviews_per_page || 10);
        setShowPhotos(result.data.show_photos !== false);
        setShowVerifiedBadge(result.data.show_verified_badge !== false);
        setShowReviewTitle(result.data.show_review_title !== false);
        
        // Review Form
        setTitleRequired(result.data.title_required || false);
        setAllowPhotoUpload(result.data.allow_photo_upload !== false);
        setMaxPhotos(result.data.max_photos || 3);
      }
      
      setLoading(false);
    };

    fetchSettings();
  }, [shopId]);

  const handleSubmit = async () => {
    if (!shopId) return;

    setSaving(true);
    setMessage(null);

    // FREE sempre deve ter require_verification = true
    const finalRequireVerification = isPaid ? requireVerification : true;

    const settings = {
      // Moderation
      auto_approve: autoApprove,
      require_verification: finalRequireVerification,
      min_comment_length: minCommentLength,
      banned_words: bannedWords.trim(),
      auto_reject_rating: autoRejectRating,
      
      // Widget Display
      review_order: reviewOrder,
      reviews_per_page: reviewsPerPage,
      show_photos: showPhotos,
      show_verified_badge: showVerifiedBadge,
      show_review_title: showReviewTitle,
      
      // Review Form
      title_required: titleRequired,
      allow_photo_upload: allowPhotoUpload,
      max_photos: maxPhotos,
    };

    const result = await settingsApi.update(shopId, settings);
    
    if (result.error) {
      setMessage({ type: 'error', text: result.error });
    } else {
      setMessage({ type: 'success', text: t('settings.saved', { defaultValue: 'Settings saved successfully!' }) });
    }
    
    setSaving(false);
  };

  if (loading) {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <BlockStack gap="300" align="center">
                  <Spinner size="large" />
                  <Text as="p" tone="subdued" alignment="center">
                    {t('common.loading', { defaultValue: 'Loading...' })}
                  </Text>
                </BlockStack>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      primaryAction={{
        content: t('common.save'),
        onAction: handleSubmit,
        loading: saving,
      }}
      secondaryActions={isPaid ? [
        {
          content: 'PRO',
        },
      ] : undefined}
    >
      <Layout>
        {message && (
          <Layout.Section>
            <Banner
              tone={message.type === 'success' ? 'success' : 'critical'}
              onDismiss={() => setMessage(null)}
            >
              {message.text}
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <Form onSubmit={handleSubmit}>
              <FormLayout>
                <Text variant="headingMd" as="h2">
                  {t('settings.moderation')}
                </Text>

                <Checkbox
                  label={t('settings.autoApprove')}
                  checked={autoApprove}
                  onChange={setAutoApprove}
                  helpText={t('settings.autoApproveHelp', { defaultValue: 'Reviews will be automatically approved without manual review' })}
                />

                <Checkbox
                  label={t('settings.requireVerification')}
                  checked={requireVerification}
                  onChange={setRequireVerification}
                  disabled={!isPaid}
                  helpText={
                    !isPaid
                      ? t('settings.requireVerificationHelpFree', { defaultValue: 'No plano FREE, apenas compradores verificados podem deixar reviews. Faça upgrade para PRO para permitir reviews de qualquer pessoa.' })
                      : t('settings.requireVerificationHelp', { defaultValue: 'Se ativado, apenas clientes que compraram o produto podem deixar reviews. Se desativado, qualquer pessoa pode deixar reviews.' })
                  }
                />
                
                <Select
                  label={t('settings.minCommentLength', { defaultValue: 'Mínimo de Caracteres no Comentário' })}
                  options={[
                    { label: '10 caracteres', value: '10' },
                    { label: '20 caracteres', value: '20' },
                    { label: '50 caracteres', value: '50' },
                    { label: '100 caracteres', value: '100' },
                  ]}
                  value={minCommentLength.toString()}
                  onChange={(value) => setMinCommentLength(parseInt(value))}
                  helpText={t('settings.minCommentLengthHelp', { defaultValue: 'Comentários com menos caracteres serão rejeitados automaticamente' })}
                />
                
                <TextField
                  label={t('settings.bannedWords', { defaultValue: 'Palavras Proibidas' })}
                  value={bannedWords}
                  onChange={setBannedWords}
                  multiline={3}
                  autoComplete="off"
                  helpText={t('settings.bannedWordsHelp', { defaultValue: 'Separe as palavras por vírgula. Reviews contendo essas palavras serão rejeitadas automaticamente.' })}
                  placeholder="exemplo, teste, spam"
                />
                
                <Select
                  label={t('settings.autoRejectRating', { defaultValue: 'Auto-rejeitar Reviews com Menos de X Estrelas' })}
                  options={[
                    { label: t('settings.autoRejectNone', { defaultValue: 'Não rejeitar automaticamente' }), value: '0' },
                    { label: '1 estrela', value: '1' },
                    { label: '2 estrelas', value: '2' },
                    { label: '3 estrelas', value: '3' },
                  ]}
                  value={autoRejectRating}
                  onChange={setAutoRejectRating}
                  helpText={t('settings.autoRejectRatingHelp', { defaultValue: 'Reviews com rating menor que o selecionado serão automaticamente rejeitadas' })}
                />
                
                {!isPaid && (
                  <Banner tone="info">
                    <Text as="p" variant="bodySm">
                      <strong>{t('settings.proFeature', { defaultValue: 'Recurso PRO:' })}</strong> {t('settings.requireVerificationProMessage', { defaultValue: 'No plano FREE, apenas compradores verificados podem deixar reviews. Faça upgrade para PRO para permitir reviews de qualquer pessoa.' })}
                    </Text>
                  </Banner>
                )}
                {isPaid && !requireVerification && (
                  <Banner tone="success">
                    <Text as="p" variant="bodySm">
                      <strong>{t('settings.openMode', { defaultValue: 'Modo Aberto:' })}</strong> {t('settings.openModeMessage', { defaultValue: 'Qualquer pessoa pode deixar reviews, mesmo sem comprar o produto.' })}
                    </Text>
                  </Banner>
                )}
              </FormLayout>
            </Form>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Form onSubmit={handleSubmit}>
              <FormLayout>
                <Text variant="headingMd" as="h2">
                  {t('settings.widgetDisplay', { defaultValue: 'Widget Display' })}
                </Text>

                <Select
                  label={t('settings.reviewOrder', { defaultValue: 'Ordenação Padrão' })}
                  options={[
                    { label: t('settings.orderNewest', { defaultValue: 'Mais Recentes' }), value: 'newest' },
                    { label: t('settings.orderOldest', { defaultValue: 'Mais Antigas' }), value: 'oldest' },
                    { label: t('settings.orderHighest', { defaultValue: 'Maior Rating' }), value: 'highest' },
                    { label: t('settings.orderLowest', { defaultValue: 'Menor Rating' }), value: 'lowest' },
                  ]}
                  value={reviewOrder}
                  onChange={setReviewOrder}
                />

                <Select
                  label={t('settings.reviewsPerPage', { defaultValue: 'Reviews por Página' })}
                  options={[
                    { label: '5', value: '5' },
                    { label: '10', value: '10' },
                    { label: '20', value: '20' },
                    { label: '50', value: '50' },
                  ]}
                  value={reviewsPerPage.toString()}
                  onChange={(value) => setReviewsPerPage(parseInt(value))}
                />

                <Checkbox
                  label={t('settings.showPhotos', { defaultValue: 'Mostrar Fotos' })}
                  checked={showPhotos}
                  onChange={setShowPhotos}
                  helpText={t('settings.showPhotosHelp', { defaultValue: 'Exibir fotos enviadas pelos clientes nas reviews' })}
                />

                <Checkbox
                  label={t('settings.showVerifiedBadge', { defaultValue: 'Mostrar Badge "Compra Verificada"' })}
                  checked={showVerifiedBadge}
                  onChange={setShowVerifiedBadge}
                  helpText={t('settings.showVerifiedBadgeHelp', { defaultValue: 'Exibir badge indicando que o cliente comprou o produto' })}
                />
                <Banner tone="info">
                  {t('settings.verifiedPurchaseInfo', { 
                    defaultValue: 'O selo "Compra Verificada" aparece automaticamente quando um cliente que comprou o produto deixa uma avaliação. O sistema verifica automaticamente se o email do cliente corresponde a um pedido que contém o produto.' 
                  })}
                </Banner>

                <Checkbox
                  label={t('settings.showReviewTitle', { defaultValue: 'Mostrar Título da Review' })}
                  checked={showReviewTitle}
                  onChange={setShowReviewTitle}
                  helpText={t('settings.showReviewTitleHelp', { defaultValue: 'Exibir título da review (se fornecido pelo cliente)' })}
                />
              </FormLayout>
            </Form>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Form onSubmit={handleSubmit}>
              <FormLayout>
                <Text variant="headingMd" as="h2">
                  {t('settings.reviewForm', { defaultValue: 'Formulário de Review' })}
                </Text>

                <Checkbox
                  label={t('settings.titleRequired', { defaultValue: 'Título Obrigatório' })}
                  checked={titleRequired}
                  onChange={setTitleRequired}
                  helpText={t('settings.titleRequiredHelp', { defaultValue: 'Tornar o campo título obrigatório no formulário de review' })}
                />

                <Checkbox
                  label={t('settings.allowPhotoUpload', { defaultValue: 'Permitir Upload de Fotos' })}
                  checked={allowPhotoUpload}
                  onChange={setAllowPhotoUpload}
                  disabled={!isPaid}
                  helpText={
                    !isPaid
                      ? t('settings.allowPhotoUploadHelpFree', { defaultValue: 'Upload de fotos disponível apenas no plano PRO' })
                      : t('settings.allowPhotoUploadHelp', { defaultValue: 'Permitir que clientes enviem fotos junto com a review' })
                  }
                />

                {isPaid && allowPhotoUpload && (
                  <>
                    <Select
                      label={t('settings.maxPhotos', { defaultValue: 'Máximo de Fotos por Review' })}
                      options={[
                        { label: '1', value: '1' },
                        { label: '3', value: '3' },
                        { label: '5', value: '5' },
                        { label: '10', value: '10' },
                      ]}
                      value={maxPhotos.toString()}
                      onChange={(value) => setMaxPhotos(parseInt(value))}
                      disabled={!allowPhotoUpload}
                      helpText={t('settings.maxPhotosHelp', { defaultValue: 'Número máximo de fotos que podem ser enviadas por review' })}
                    />
                  </>
                )}

                {!isPaid && (
                  <Banner tone="info">
                    <Text as="p" variant="bodySm">
                      <strong>{t('settings.proFeature', { defaultValue: 'Recurso PRO:' })}</strong> {t('settings.photoUploadProMessage', { defaultValue: 'Upload de fotos disponível apenas no plano PRO. Faça upgrade para desbloquear este recurso.' })}
                    </Text>
                  </Banner>
                )}
              </FormLayout>
            </Form>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default Settings;

